/**
 * DraftAutocompleteField Component
 *
 * A searchable select/autocomplete field that integrates with the draft state pattern.
 * Uses design system styling for consistent aesthetics.
 */

import React, { useState, type SyntheticEvent } from 'react';
import { Grid, Autocomplete, TextField, Chip, Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii } from '../../tokens';

export interface DraftAutocompleteOption {
    id: string;
    name: string;
}

export interface DraftAutocompleteFieldProps<State extends Record<string, any>> {
    /** Field label */
    label: string;
    /** ID key in state */
    idKey: string & keyof State;
    /** Name key in state (optional - defaults to idKey) */
    nameKey?: string & keyof State;
    /** Draft state tuple [state, setState] */
    draft: [State, React.Dispatch<React.SetStateAction<State>>];
    /** Available options */
    options: DraftAutocompleteOption[];
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Whether to include "All" option */
    includeAll?: boolean;
    /** Custom onChange handler */
    onChange?: (event: SyntheticEvent<Element, Event>, value: any) => void;
    /** Grid size (out of 12) */
    size?: number;
    /** Whether the field is required */
    required?: boolean;
    /** Whether the field is loading */
    loading?: boolean;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const DraftAutocompleteField = <State extends Record<string, any>>({
    label,
    idKey,
    nameKey,
    draft,
    options,
    disabled = false,
    includeAll = false,
    onChange,
    size = 12,
    required = false,
    loading = false,
    sx,
}: DraftAutocompleteFieldProps<State>) => {
    const [draftState, setDraftState] = draft;
    const [open, setOpen] = useState(false);

    const allOptions = includeAll ? [{ id: '*', name: 'All ' }, ...options] : options;

    const handleOpen = () => {
        if (!disabled) setOpen(true);
    };

    const selectedValue =
        allOptions?.filter(({ id }) => {
            const match =
                typeof draftState?.[idKey] === 'number'
                    ? parseInt(id) === draftState?.[idKey]
                    : id === draftState?.[idKey];
            return match;
        }) || [];

    const handleChange = (event: SyntheticEvent<Element, Event>, value: DraftAutocompleteOption[]) => {
        if (onChange) {
            onChange(event, value);
        } else {
            const { id, name } = value.pop?.() ?? {};

            const effectiveNameKey = nameKey || idKey;
            const updated =
                idKey === effectiveNameKey
                    ? { ...draftState, [effectiveNameKey]: name ?? id }
                    : {
                          ...draftState,
                          [idKey]: id && parseInt(id) ? parseInt(id) : id,
                          [effectiveNameKey]: name,
                      };

            setDraftState(updated);
        }
    };

    return (
        <Grid item xs={12} sm={12} md={size} sx={sx}>
            <Box>
                {/* Standalone Label */}
                <Typography
                    component='label'
                    variant='body2'
                    sx={{
                        display: 'block',
                        mb: '6px',
                        fontWeight: typographyTokens.fontWeight.medium,
                        color: colors.neutral[500],
                        fontSize: typographyTokens.fontSize.sm,
                    }}>
                    {label}
                    {required && <span style={{ color: colors.error.main, marginLeft: 4 }}>*</span>}
                </Typography>

                <Autocomplete
                    open={open}
                    multiple
                    size='small'
                    openOnFocus
                    disabled={disabled}
                    loading={loading}
                    value={selectedValue}
                    options={allOptions}
                    getOptionLabel={({ name }) => name}
                    filterSelectedOptions
                    onChange={handleChange}
                    onOpen={handleOpen}
                    onClose={() => setOpen(false)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant='outlined'
                            placeholder={`Select ${label}`}
                            InputLabelProps={{ shrink: false }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    minHeight: 40,
                                    fontSize: typographyTokens.fontSize.base,
                                    backgroundColor: colors.neutral[0],
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.neutral[300],
                                        borderRadius: `${radii.md}px`,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.neutral[400],
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.primary[500],
                                        borderWidth: 2,
                                    },
                                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.neutral[200],
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: colors.neutral[400],
                                    opacity: 1,
                                },
                            }}
                        />
                    )}
                    ChipProps={{
                        sx: { pointerEvents: disabled ? 'none' : 'auto' },
                    }}
                    renderOption={(props, option) => (
                        <li
                            {...props}
                            key={option.id}
                            style={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: colors.neutral[700],
                                padding: '8px 12px',
                            }}>
                            {option.name}
                        </li>
                    )}
                    renderTags={(selected, getTagProps) =>
                        selected.map((value, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={value.id}
                                label={value.name}
                                size='small'
                                disabled={disabled}
                                onClick={(e) => disabled && e.stopPropagation()}
                                sx={{
                                    backgroundColor: colors.primary[100],
                                    color: colors.primary[700],
                                    fontWeight: typographyTokens.fontWeight.medium,
                                    fontSize: typographyTokens.fontSize.xs,
                                    borderRadius: `${radii.md}px`,
                                    '& .MuiChip-deleteIcon': {
                                        color: colors.primary[500],
                                        '&:hover': {
                                            color: colors.primary[700],
                                        },
                                    },
                                }}
                            />
                        ))
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    sx={{
                        '& .MuiAutocomplete-paper': {
                            borderRadius: `${radii.md}px`,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        },
                    }}
                />
            </Box>
        </Grid>
    );
};

export default DraftAutocompleteField;
